import { useEffect, useRef } from "react";
import { useUser } from "@/hooks/useUser";
import { normalizeLanguagePreference, setLanguage as setI18nLanguage } from "@/i18n";
import { fetchUserSettings, isMissingProfileError } from "@/lib/users-api";
import i18n from "@/i18n";
import { useLocale } from "@/i18n/LocaleContext";

/**
 * Hydrates account language on sign-in, respecting URL language. Currency is
 * independently owned by CurrencyProvider so URL markets cannot block it.
 */
export function PreferenceHydrator() {
  const { session } = useUser();
  // The URL is the source of truth for market & language (task #865). Anything
  // pinned in the URL — e.g. /fr or /de/en — wins over the saved preference.
  // Without this guard a German visitor on /uk/en would have their UI flipped
  // to German on sign-in, contradicting the explicit URL.
  const { market: urlMarket, language: urlLanguage } = useLocale();
  const hydratedFor = useRef<string | null>(null);

  useEffect(() => {
    const token = session?.access_token;
    const userId = session?.user?.id || null;
    if (!token || !userId) {
      hydratedFor.current = null;
      return;
    }
    if (hydratedFor.current === userId) return;
    hydratedFor.current = userId;

    let cancelled = false;
    fetchUserSettings(token)
      .then((data) => {
        if (cancelled) return;
        // Currency hydration is owned by CurrencyProvider, with account and
        // selection-revision guards. URL markets only pin language.
        // Language: skip if the URL has an explicit /<lang> segment OR if the
        // market's default language is implicitly active (bare /uk → en).
        const urlPinsLanguage = Boolean(urlLanguage) || Boolean(urlMarket);
        if (!urlPinsLanguage && (data.language || data.preferred_language)) {
          const lang = normalizeLanguagePreference(data.language || data.preferred_language);
          if (lang && lang !== i18n.language) {
            void setI18nLanguage(lang);
          }
        }
      })
      .catch((err) => {
        // Task #5423 — an authenticated user with a valid session but no
        // public.users row yet (legacy-migrated / no-email account) gets a
        // 404 "User profile not found" here. That is an expected state, NOT a
        // failure: there are no saved preferences to hydrate until the
        // profile-completion flow creates the row, and the
        // ProfileCompletionWatcher is already prompting the user. Keep
        // `hydratedFor` pinned so we don't re-issue the same doomed call on
        // every render — retryProfile()/completion will re-mount this with a
        // real row. Only genuine failures should clear the ref for a retry.
        if (isMissingProfileError(err)) return;
        // Allow a retry on next session change if the call failed.
        hydratedFor.current = null;
      });

    return () => {
      cancelled = true;
    };
  }, [session?.access_token, session?.user?.id, urlMarket, urlLanguage]);

  return null;
}
